/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState } from 'react';
import { Cloud, RefreshCw, Wifi, Check, X } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import type { GCPProjectOrgSettings } from '@shared/types/organizationSettings';

interface GCPConnectionStatus {
  tested: boolean;
  success: boolean;
  error?: string;
  details?: {
    projectId?: string;
    displayName?: string;
    state?: string;
  };
}

interface GCPProjectSectionProps {
  settings: GCPProjectOrgSettings;
  onChange: (settings: GCPProjectOrgSettings) => void;
  onDirty: () => void;
}

export function GCPProjectSection({ 
  settings, 
  onChange, 
  onDirty 
}: GCPProjectSectionProps) {
  const { toast } = useToast();
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<GCPConnectionStatus>({ 
    tested: false, 
    success: false 
  });

  const testConnection = async () => {
    if (!settings.projectId) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: 'Please provide a GCP Project ID'
      });
      return;
    }
    
    setIsTestingConnection(true);
    setConnectionStatus({ tested: false, success: false });
    
    try {
      const response = await fetch('/api/settings/gcp-project/test-connection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: settings.projectId,
          region: settings.region
        })
      });
      
      const result = await response.json();
      
      if (result.success && result.data?.success) {
        setConnectionStatus({ 
          tested: true, 
          success: true,
          details: result.data.details
        });
        toast({
          title: 'Connection successful',
          description: result.data.message || 'Successfully connected to GCP project',
        });
      } else {
        setConnectionStatus({
          tested: true,
          success: false,
          error: result.data?.message || result.error?.message || 'Connection failed'
        });
      }
    } catch (error: any) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: error.message || 'Connection test failed'
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  return (
    <div style={{ 
      marginBottom: '32px',
      padding: '20px',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '8px',
      border: '1px solid #86efac'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Cloud size={20} style={{ marginRight: '8px', color: '#16a34a' }} />
        <h3 style={{ margin: 0, color: '#14532d' }}>Google Cloud Platform Project</h3>
      </div>
      
      <p style={{ fontSize: '13px', color: '#166534', marginBottom: '16px' }}>
        Configure a default GCP project for cloud integrations. This project ID will be used as the default for 
        Vertex AI (LLM) and GCS (data storage) operations, but can be overridden in each integration's settings.
      </p>
      
      <div style={{ 
        padding: '16px', 
        background: '#fff',
        borderRadius: '6px',
        border: '1px solid #bbf7d0'
      }}>
        <div className="form-group">
          <label htmlFor="gcp-project-id">Default GCP Project ID</label>
          <input
            id="gcp-project-id"
            type="text"
            placeholder="my-gcp-project"
            value={settings.projectId}
            onChange={(e) => {
              onChange({ ...settings, projectId: e.target.value });
              onDirty();
            }}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
          <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
            This GCP project will be used by default for Vertex AI and GCS integrations
          </small>
        </div>
        
        <div className="form-group">
          <label htmlFor="gcp-region">Default GCP Region</label>
          <select
            id="gcp-region"
            value={settings.region}
            onChange={(e) => {
              onChange({ ...settings, region: e.target.value });
              onDirty();
            }}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value="us-central1">us-central1 (Iowa)</option>
            <option value="us-east1">us-east1 (South Carolina)</option>
            <option value="us-east4">us-east4 (Virginia)</option>
            <option value="us-west1">us-west1 (Oregon)</option>
            <option value="us-west4">us-west4 (Nevada)</option>
            <option value="europe-west1">europe-west1 (Belgium)</option>
            <option value="europe-west4">europe-west4 (Netherlands)</option>
            <option value="asia-southeast1">asia-southeast1 (Singapore)</option>
          </select>
          <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
            Default region for GCP resources
          </small>
        </div>
        
        <div style={{ 
          marginTop: '12px',
          padding: '12px',
          background: '#f0fdf4',
          borderRadius: '4px',
          border: '1px solid #bbf7d0',
          fontSize: '13px',
          color: '#166534'
        }}>
          <strong>Authentication:</strong> Ensure you have authenticated to GCP with application-default credentials:
          <br />
          <code style={{ 
            display: 'block', 
            marginTop: '8px', 
            padding: '8px', 
            background: '#dcfce7', 
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '12px'
          }}>
            gcloud auth application-default login
          </code>
        </div>
        
        {/* Test Connection Button */}
        <div style={{ marginTop: '16px' }}>
          <button
            className="btn"
            onClick={testConnection}
            disabled={isTestingConnection || !settings.projectId}
            style={{
              width: 'auto',
              padding: '8px 16px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {isTestingConnection ? (
              <>
                <RefreshCw size={16} className="spin" />
                Testing Connection...
              </>
            ) : (
              <>
                <Wifi size={16} />
                Test Connection
              </>
            )}
          </button>
          
          {connectionStatus.tested && (
            <div style={{ marginTop: '12px' }}>
              {connectionStatus.success ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <Check size={16} style={{ color: '#28a745', marginRight: '8px' }} />
                    <span style={{
                      padding: '4px 12px',
                      background: '#d1fae5',
                      color: '#065f46',
                      borderRadius: '4px',
                      fontSize: '13px',
                      fontWeight: 500
                    }}>
                      Connected to GCP project
                    </span>
                  </div>
                  {connectionStatus.details && (
                    <div style={{
                      padding: '8px 12px',
                      background: '#f0fdf4',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#166534'
                    }}>
                      <div><strong>Project ID:</strong> {connectionStatus.details.projectId}</div>
                      {connectionStatus.details.displayName && (
                        <div><strong>Display Name:</strong> {connectionStatus.details.displayName}</div>
                      )}
                      {connectionStatus.details.state && (
                        <div><strong>State:</strong> {connectionStatus.details.state}</div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <X size={16} style={{ color: '#dc3545', marginRight: '8px' }} />
                  <span style={{
                    padding: '4px 12px',
                    background: '#fef2f2',
                    color: '#b91c1c',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontWeight: 500
                  }}>
                    {connectionStatus.error || 'Connection failed'}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
